/* eslint-disable react/no-danger */
import fetch from "isomorphic-unfetch";
import Image from "next/future/image";
import Link from "next/link";
import React, { Component, useState } from "react";
import sanitizeHtml from "sanitize-html";
import slugify from "slugify";
import styled from "styled-components";
import ClientSpotlightCard from "@/components/ClientSpotlightCard/ClientSpotlightCard";
import FooterComponent from "@/components/Footer/FooterComponent";
import Header from "@/components/Header/Header";
import SEOHead from "@/components/SEOHead/SEOHead";
import { WordPressAPI } from "../../../config";
import dynamic from 'next/dynamic';

const PullUpSchedulerV2 = dynamic(
  () => import('@components/PullUpScheduler/PullUpSchedulerV2'),
  { ssr: false }
);

const ClientSpotlight = styled.section`
  p {
    font-weight: 400;
  }
  .action-buttons {
    text-align: center;
    .btn {
      margin: 0 auto 2rem 0;
    }
  }
  .disclaimer {
    margin-top: 3em;
    font-size: 0.9125rem;
    font-style: italic;
    a {
      font-weight: 700;
    }
  }
  .small-image {
    display: block;
    margin: 20px auto;
    width: 100%;
    text-align: center;
    ${(props) => props.theme.media.sm} {
      margin: 0 0 20px 20px;
      float: right;
    }
    ${props => props.theme.media.md} {
      width: auto;
    }
    ${props => props.theme.media.lg} {
      width: 60%;
    }
    img {
      width: 100%;
      border: 1px solid ${(props) => props.theme.gray100};
      padding: 5px;
      border-radius: 10px;
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
      ${props => props.theme.media.md} {
        width: auto;
      }
      ${props => props.theme.media.lg} {
        width: 100%;
      }
    }
  }
  .large-image {
    margin-bottom: 30px;
  }
`;

const Related = styled.section`
  padding-top: 60px;
  border-top: 1px solid ${(props) => props.theme.gray200};
  h2 {
    color: ${(props) => props.theme.primary};
  }
`;

const generateRelated = (related) => (
  <Related>
    <div className="container" data-component="cs-related-articles">
      <h2>Related Industry Spotlights</h2>
      <div className="row justify-content-center">
        {related.map((r) => (
          <div className="col-sm-6 col-md-4 col-lg-3">
            <ClientSpotlightCard client={r} related />
          </div>
        ))}
      </div>
    </div>
  </Related>
);

const getCurrentClient = (clients, slug) =>
  clients.find((element) => element.slug === slug);


const SingleClientSpotlight = ({client, data, relatedSpotlight}) => {
  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);

  const {
    additional_questions,
    lead_values,
    phone_number_setup,
  } = data.acf;

  function showImage() {
    if (client.large_image) {
      return (
        <Image
          className="large-image"
          data-component="cs-thumbnail"
          src={client.large_image.url}
          alt={client.large_image.alt}
          priority
          width={1200}
          height={600}
          layout="raw"
        />
      );
    }
    return (
      <Image
        className="small-image"
        data-component="cs-thumbnail"
        src={client.small_image.url}
        alt={client.small_image.alt}
        priority
        width={1200}
        height={600}
        layout="raw"
      />
    );
  }

  return (
    <div>
      <SEOHead>
        <title>{client.title}</title>
        <meta
          name="description"
          content={client.meta_description || ""}
          key="description"
        />
      </SEOHead>
      <Header 
            PaidCampaign={true} 
            AdditionalQuestions={additional_questions}
            className="mobile-header"
            phone_number={phone_number_setup}
            leadValues={lead_values} />
      <ClientSpotlight data-component="cs-article-content">
        <div className="container">
          <h1>{client.title}</h1>
          <div className="row justify-content-center">
            <div className="col-xl-10">
              <div className={client.large_image ? 'large-image' : 'small-image'}>
                {showImage()}
                <div className="action-buttons">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setIsSchedulerOpen(true) }
                  >
                    Schedule My Free Call
                  </button>
                </div>
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(client.content, {
                    allowedTags: sanitizeHtml.defaults.allowedTags,
                  }),
                }}
              />
              <div className="disclaimer">
                1-800Accountant is changing the way businesses connect with
                their accounting and taxes. Through the use of an innovative
                and easy-to-use technology platform that combines pro-active,
                year-round advice and planning from a team of accounting
                professionals, we are able to deliver accounting services both
                seamlessly and more affordably than ever before.{" "}
                <Link href="/join-now">
                  <a>Schedule a consultation</a>
                </Link>{" "}
                with a Small Business Tax Specialist.
              </div>
            </div>
          </div>
        </div>
      </ClientSpotlight>
      {generateRelated(relatedSpotlight)}
      <FooterComponent phone_number={phone_number_setup} />

      {isSchedulerOpen && (
        <PullUpSchedulerV2
          AdditionalQuestions={additional_questions}
          leadValues={lead_values}
          setIsSchedulerOpen={setIsSchedulerOpen}
          PaidCampaign={true}
        />
      )}
    </div>
  );
}

export async function getServerSideProps({ res, query }) {
  const response = await fetch(`${WordPressAPI.pages}/324`);
  const data = await response.json();
  const client = getCurrentClient(data.acf.client, query.slug);

  if (client === undefined) {
    res.status(404).redirect("/");
  }

  const relatedSpotlight = data.acf.client
    .filter((c) => {
      const clientIndustry = slugify(c.industry, { lower: true });
      if (clientIndustry === query.industry && c.slug !== query.slug) {
        return c;
      }
      return null;
    })
    .sort(() => 0.5 - Math.random())
    .slice(0, 4);

    return {
      props: {
        data: data,
        client: client,
        relatedSpotlight: relatedSpotlight
      }, // will be passed to the page component as props
    };
};

export default SingleClientSpotlight;
